import { getCategoryUrl } from './urls';

export const getCategoryParentId = (category) => {
  const parentId = category?.parentId;
  if (parentId === null || parentId === undefined || parentId === '') {
    return null;
  }
  return Number(parentId);
};

export const buildCategoryTree = (categories = [], options = {}) => {
  const { onlyPublished = false } = options;
  const list = categories.filter((category) => !onlyPublished || category.isPublished !== false);
  const nodes = new Map(
    list.map((category) => [category.id, { ...category, children: [] }])
  );
  const roots = [];

  list.forEach((category) => {
    const node = nodes.get(category.id);
    const parentId = getCategoryParentId(category);
    if (parentId && nodes.has(parentId)) {
      nodes.get(parentId).children.push(node);
    } else {
      roots.push(node);
    }
  });

  const sortNodes = (items) => {
    items.sort((a, b) => String(a.name).localeCompare(String(b.name), 'uk'));
    items.forEach((item) => sortNodes(item.children));
  };

  sortNodes(roots);
  return roots;
};

export const flattenCategoryTree = (tree = [], depth = 0) => {
  const result = [];

  tree.forEach((node) => {
    const { children, ...category } = node;
    result.push({ ...category, depth });
    if (children.length) {
      result.push(...flattenCategoryTree(children, depth + 1));
    }
  });

  return result;
};

export const getHierarchicalCategories = (categories = [], options = {}) => {
  return flattenCategoryTree(buildCategoryTree(categories, options));
};

export const getCategoryIndentLabel = (name, depth, prefix = '— ') => {
  if (!depth) return name;
  return `${prefix.repeat(depth)}${name}`;
};

const collectDescendantIds = (categories, rootId) => {
  const ids = new Set();
  const walk = (parentId) => {
    categories.forEach((category) => {
      if (getCategoryParentId(category) === parentId) {
        ids.add(category.id);
        walk(category.id);
      }
    });
  };
  walk(rootId);
  return ids;
};

export const getParentCategoryOptions = (categories = [], selfId = null) => {
  const self = selfId ? Number(selfId) : null;
  const excluded = self ? new Set([self, ...collectDescendantIds(categories, self)]) : new Set();

  return getHierarchicalCategories(categories).filter((category) => !excluded.has(category.id));
};

export const filterCategoriesHierarchical = (categories = [], query = '', options = {}) => {
  const normalized = query.trim().toLowerCase();
  const hierarchical = getHierarchicalCategories(categories, options);
  if (!normalized) return hierarchical;

  return hierarchical.filter((category) =>
    String(category.name || '').toLowerCase().includes(normalized)
  );
};

export const getProductCategoryId = (product) => {
  const id = product?.category?.id ?? product?.categoryId ?? null;
  if (id === null || id === undefined || id === '') {
    return null;
  }
  return Number(id);
};

/** ID категорий, к которым привязан хотя бы один товар. */
export const getCategoryIdsWithProducts = (products = []) => {
  const ids = new Set();
  products.forEach((product) => {
    const categoryId = getProductCategoryId(product);
    if (categoryId != null) {
      ids.add(categoryId);
    }
  });
  return ids;
};

const hasProductsInSubtree = (node, categoryIdsWithProducts) => {
  if (categoryIdsWithProducts.has(Number(node.id))) {
    return true;
  }
  return (node.children || []).some((child) => hasProductsInSubtree(child, categoryIdsWithProducts));
};

/** Дерево категорий: остаются ветки, где есть товар в категории или подкатегории. */
export const filterCategoryTreeByProducts = (tree = [], categoryIdsWithProducts) => {
  const result = [];

  tree.forEach((node) => {
    if (!hasProductsInSubtree(node, categoryIdsWithProducts)) {
      return;
    }
    result.push({
      ...node,
      children: filterCategoryTreeByProducts(node.children || [], categoryIdsWithProducts),
    });
  });

  return result;
};

export const getCategoryTreeWithProducts = (categories = [], products = [], options = {}) => {
  const tree = buildCategoryTree(categories, options);
  const categoryIdsWithProducts = getCategoryIdsWithProducts(products);
  return filterCategoryTreeByProducts(tree, categoryIdsWithProducts);
};

/** Плоский список категорий, в которых есть товар (без пустых родителей). */
export const getCategoriesWithDirectProducts = (categories = [], products = [], options = {}) => {
  const categoryIdsWithProducts = getCategoryIdsWithProducts(products);
  return getHierarchicalCategories(categories, options).filter((category) =>
    categoryIdsWithProducts.has(Number(category.id))
  );
};

/** ID предков категории (для раскрытия меню). */
export const getCategoryBreadcrumbTrail = (categories = [], categoryId) => {
  const targetId = Number(categoryId);
  if (!targetId) {
    return [];
  }

  const trail = [];
  let current = categories.find((item) => Number(item.id) === targetId);

  while (current) {
    trail.unshift({
      id: current.id,
      name: current.name,
      path: getCategoryUrl(current),
    });
    const parentId = getCategoryParentId(current);
    if (!parentId) {
      break;
    }
    current = categories.find((item) => Number(item.id) === parentId);
  }

  return trail;
};

export const getCategoryAncestorIds = (categories = [], categoryId) => {
  const targetId = Number(categoryId);
  if (!targetId) {
    return [];
  }

  const ancestors = [];
  let current = categories.find((item) => Number(item.id) === targetId);

  while (current) {
    const parentId = getCategoryParentId(current);
    if (!parentId) {
      break;
    }
    ancestors.push(parentId);
    current = categories.find((item) => Number(item.id) === parentId);
  }

  return ancestors;
};
